import { Box, type BoxProps, type SxProps, type Theme } from "@mui/material";
import { lighten } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { GRID_FONT, GRID_TINT, GRID_Z, INK, SHADOW, SURFACE } from "../../../../theme/tokens";
import { ROW_H } from "../gridGeometry";

/**
 * Emphasis of a sticky leading cell — the one knob that captures the real,
 * pre-existing drift between the three grids (Phase A snapped everything else to
 * tokens). Header:
 *  - `muted`    — Coverage Summary label row (SM / MUTED ink / WIDE tracking).
 *  - `standard` — Detail + People label row (BASE / SECONDARY ink / HEADER tracking).
 * Body:
 *  - `primary`   — the first identity column (PRIMARY ink, 600, ellipsis).
 *  - `secondary` — the other identity columns (SECONDARY ink, 500, ellipsis).
 *  - `plain`     — People's cells, which set ink/weight/size on their own children
 *    (no defaults, no ellipsis).
 */
export type StickyLeadingEmphasis = "muted" | "standard" | "primary" | "secondary" | "plain";

type StickyLeadingCellProps = Omit<BoxProps, "left"> & {
	/** Sticky `left` offset — 0 for the first column, a leading-offset for the rest. */
	left: string | number;
	/** `header` = the frozen label row (row 1/2); `body` = a per-row identity cell. */
	variant: "header" | "body";
	emphasis?: StickyLeadingEmphasis;
	/** Body-only: min row height (default `ROW_H`; Detail passes `EXPANDED_ROW_MIN_H`). */
	minHeight?: string;
	/** Lift the frozen zone over the day cells with the right-edge shadow (last sticky column). */
	showEdgeShadow?: boolean;
	/** Inner gap (theme spacing units) between the cell's children. */
	gap?: number;
	/** Button type when rendered as `component="button"` (sortable headers). */
	type?: "button" | "submit" | "reset";
};

const baseSx =
	({
		left,
		variant,
		emphasis,
		minHeight,
		showEdgeShadow,
		gap,
	}: Required<Pick<StickyLeadingCellProps, "left" | "variant">> & Omit<StickyLeadingCellProps, "left" | "variant">) =>
	(theme: Theme): SystemStyleObject<Theme> => {
		const edge = showEdgeShadow ? { boxShadow: SHADOW.STICKY_EDGE } : {};
		const gapSx = gap === undefined ? {} : { gap };
		if (variant === "header") {
			const muted = emphasis === "muted";
			return {
				display: "flex",
				alignItems: "center",
				px: 1.5,
				position: "sticky",
				left,
				zIndex: GRID_Z.CORNER,
				backgroundColor: lighten(theme.palette.primary.main, GRID_TINT.LEADING_HEADER_BG),
				fontSize: muted ? GRID_FONT.SM : GRID_FONT.BASE,
				fontWeight: 700,
				color: muted ? INK.MUTED : INK.SECONDARY,
				textTransform: "uppercase",
				letterSpacing: muted ? GRID_FONT.LETTER_SPACING.WIDE : GRID_FONT.LETTER_SPACING.HEADER,
				...gapSx,
				...edge,
			};
		}
		const plain = emphasis === "plain";
		return {
			display: "flex",
			alignItems: "center",
			px: 1.5,
			minHeight: minHeight ?? ROW_H,
			position: "sticky",
			left,
			zIndex: GRID_Z.STICKY_CELL,
			backgroundColor: SURFACE,
			whiteSpace: "nowrap",
			overflow: "hidden",
			...gapSx,
			...edge,
			...(plain
				? {}
				: {
						fontSize: GRID_FONT.BASE,
						color: emphasis === "primary" ? INK.PRIMARY : INK.SECONDARY,
						fontWeight: emphasis === "primary" ? 600 : 500,
						textOverflow: "ellipsis",
					}),
		};
	};

/**
 * The single frozen (sticky-left) identity cell for every Master Schedule v3
 * grid — header labels and per-row identity columns alike. Replaces the forked
 * `leadingHeaderSx` / `leadingCellSx` factories the three grids each carried.
 * All non-styling knobs (top offset, `gridColumn`, `component`, click handlers,
 * `data-testid`, focus styles) pass straight through as `BoxProps` + `sx`.
 */
export const StickyLeadingCell = ({
	left,
	variant,
	emphasis = variant === "header" ? "standard" : "secondary",
	minHeight,
	showEdgeShadow,
	gap,
	sx,
	...rest
}: StickyLeadingCellProps) => (
	<Box sx={[baseSx({ left, variant, emphasis, minHeight, showEdgeShadow, gap }), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>} {...rest} />
);
